import React, { Fragment, useState } from 'react';
import Typography from '@material-ui/core/Typography';
import DetailsRow from '../../../../../shared/components/UI/DetailsRow';
import { useDetailsStyles } from '../../../../../shared/styles/common/useDetailsStyles';
import GlobalButton from '../../../../../shared/components/UI/GlobalButton';
import FullScreenModal from '../../../../../shared/components/fullscreenModal/FullscreenModal';
import ContractPreview from '../../../../../shared/components/job/ContractPreview';
import SignaturePadModal from '../../../../../shared/components/signaturePad/SignaturePadModal';
import NoContract from './JobContractTabNoContract';
import { JOB_STATUSES } from '../../../../../shared/constants';
import DetailsRowDate from '../../../../../shared/components/UI/DetailsRowDate';
import getJobContractPreviewQuery from '../../queries/getJobContractPreviewQuery';
import SignJobContractMutation from '../../mutations/SignJobContractMutation';
import { dataURLtoFile } from '../../../../../shared/utils/helpers';
import { successToast, errorToast } from '../../../../../shared/toasts';

const JobContractTabTopSection = props => {
  const classes = useDetailsStyles();
  const {
    job: { id: jobId, status, contract, canSign, cannotSignReason },
    handleSendReminder,
    isSubmitting,
    handleSendReminderToFollowers,
    isSubmittingReminderToFollowers
  } = props;
  const [showContract, setShowContract] = useState(false);
  const [jobContractPreviewPdf, setJobContractPreviewPdf] = useState(null);
  const [openSignaturePad, setOpenSignaturePad] = useState(false);
  const [isSubmittingContract, setIsSubmittingContract] = useState(false);

  const pendingContract = contract
    ? !contract.signedPdfByContractorUrl && contract.signedPdfByOrganizationUrl
    : false;
  const signedContract = contract ? contract.signedPdfByContractorUrl : false;
  const finalizedContract = contract ? contract.finalizedPdfUrl : false;
  const contractPreviewPdf = finalizedContract || signedContract || pendingContract;

  const toggleShowContract = () => {
    setShowContract(!showContract);
  };

  const handleDownloadContract = () => {
    window.open(jobContractPreviewPdf || contractPreviewPdf);
  };

  const handlePreviewAndSignContract = () => {
    getJobContractPreviewQuery(jobId)
      .then(data => {
        if (data.pdfFile) {
          setJobContractPreviewPdf('data:application/pdf;base64,' + data.pdfFile);
          setShowContract(true);
        } else if (data.errors) {
          errorToast('Failed to generate contract preview: ' + data.errors.join(', '));
        } else {
          errorToast('Failed to generate contract preview');
        }
      })
      .catch(error => {
        console.error('Contract preview error:', error);
        errorToast('Failed to generate contract preview');
      });
  };

  const handleOpenSignaturePad = () => {
    setOpenSignaturePad(true);
  };

  const handleCloseSignaturePad = () => {
    setOpenSignaturePad(false);
  };

  const handleSignAndSend = signature => {
    if (signature) {
      setIsSubmittingContract(true);
      const signatureFile = dataURLtoFile(signature, `organizationSignature.png`);
      SignJobContractMutation(
        jobId, 
        signatureFile, 
        new Date().toISOString().split('T')[0], // Today's date
        response => {
          setIsSubmittingContract(false);
          if (response && response.job) {
            setShowContract(false);
            setOpenSignaturePad(false);
            successToast('Contract was signed successfully!');
            // Refresh the page to show updated job status
            window.location.reload();
          } else {
            errorToast('Failed to sign contract');
          }
        }
      );
    }
  };

  return (
    <Fragment>
      <Typography variant="h3">Contract Details</Typography>
      {status === JOB_STATUSES.ready_for_signature ? (
        <div className={classes.marginTop}>
          <Typography variant="body1" style={{ marginBottom: '8px' }}>
            This job is ready for signature. Click the button below to preview and sign the contract.
          </Typography>
          <div className={classes.buttonRow}>
            { canSign? (
            <GlobalButton
              id="previewAndSignContract"
              variant="primary"
              handleClick={handlePreviewAndSignContract}
            >
              Preview and Sign Contract
            </GlobalButton>
            ):(<p>{cannotSignReason}</p>)}
            </div>
            <div style={{ marginTop: '24px' }}>
              <Typography variant="body1" style={{ marginBottom: '8px' }}>
                Click the button below to send a reminder to job followers that this contract is ready for signature.
              </Typography>
              <div className={classes.buttonRow}>
                <GlobalButton
                  id="sendReminderToFollowers"
                  handleClick={handleSendReminderToFollowers}
                  disabled={isSubmittingReminderToFollowers}
                  loading={isSubmittingReminderToFollowers}
                >
                  Send Reminder to Followers
                </GlobalButton>
              </div>
            </div>
        </div>
      ) : contract ? (
        <div className={classes.contractGridContainer}>
          <div className={classes.contractFirst}>
            <div className={classes.containerColumn}>
              <Fragment>
                <DetailsRow title="Contract ID" content={contract.contractId} titleWidth={160} />
                <DetailsRow title="Job ID" content={jobId} titleWidth={160} />
                <DetailsRow
                  title="Contract Template Used"
                  content={contract.template.name}
                  titleWidth={160}
                />
              </Fragment>
            </div>
          </div>
          <div className={classes.contractSecond}>
            <div className={classes.containerColumn}>
              <Fragment>
                <DetailsRowDate
                  title="Date Signed & Sent by Org"
                  date={contract.dateSignedByOrganization}
                  titleWidth={160}
                />
                <DetailsRowDate
                  title="Date Signed by Contractor"
                  date={contract.dateSignedByContractor}
                  titleWidth={160}
                />
                <div className={classes.buttonRow}>
                  {pendingContract || signedContract ? (
                    <Fragment>
                      <GlobalButton
                        id="viewPendingContractJobContract"
                        variant="secondary"
                        handleClick={toggleShowContract}
                      >
                        {`View ${pendingContract ? 'pending' : 'signed'} contract`}
                      </GlobalButton>
                    </Fragment>
                  ) : null}

                  {status === JOB_STATUSES.awaiting_signature ? (
                    <GlobalButton
                      id="sendReminderJobContract"
                      handleClick={handleSendReminder}
                      disabled={isSubmitting}
                    >
                      Send reminder to contractor
                    </GlobalButton>
                  ) : null}
                </div>
              </Fragment>
            </div>
          </div>
        </div>
      ) : (
        <div className={classes.marginTop}>
          <NoContract />
        </div>
      )}

      <div className={classes.borderMarginTop}></div>
      <Typography variant="h3">Associated Files</Typography>
      <div className={classes.marginTop}></div>
      
      {(contractPreviewPdf || jobContractPreviewPdf) && (
        <FullScreenModal
          open={showContract}
          handleBack={toggleShowContract}
          actions={[
            ...(status === JOB_STATUSES.ready_for_signature
              ? [
                  {
                    label: 'Sign',
                    handler: handleOpenSignaturePad,
                    isSubmitting: isSubmittingContract
                  }
                ]
              : [
                  {
                    label: 'Download PDF',
                    handler: handleDownloadContract
                  }
                ])
          ]}
        >
          <ContractPreview contractPreviewPdf={jobContractPreviewPdf || contractPreviewPdf} />
        </FullScreenModal>
      )}
      
      <SignaturePadModal
        open={openSignaturePad}
        handleClose={handleCloseSignaturePad}
        handleInsertSignature={handleSignAndSend}
      />
    </Fragment>
  );
};

export default JobContractTabTopSection;
